// Hub live vault events — Server-Sent Events subscriber.
// Loaded once per page (idempotent guard on window.hubEvents). Listens to
// /api/events/stream and surfaces vault changes:
//   1. Dispatches a global DOM event: hub:vault:change (CustomEvent, detail={kind,id,op,path,ts})
//   2. Exposes window.hubEvents.on(kind, fn) for typed subscription
//   3. Default behaviour: if the change targets the entity the user is
//      currently looking at, show a toast and request ONE coalesced soft
//      refresh via window.hubRequestRefresh (below).
//
// Refresh coordinator: every programmatic refresh source — vault watcher
// bursts, .refresh touchfiles, agent-run completion, assistant hub_actions —
// funnels through window.hubRequestRefresh. Requests inside a window are
// coalesced into a single soft refresh (page-supplied window.hubPageRefresh
// wins, else window.hubSoftReload({fallback:false})). While an agent run is
// active (agent-run started frame from the backend) refreshes are HELD and
// flushed once on completion, so a run that writes ten files causes exactly
// one refresh. Hard reloads (window.location.reload) never happen from
// vault or agent events.
//
// Loop suppression: a per-tab client id is sent as ?cid=…; the server drops
// echo events that were attributed (server-side) to that same tab.

(function () {
  if (typeof window === 'undefined') return;
  if (window.hubEvents) return;

  const STORAGE_KEY = 'hub-events-client-id';
  function clientId() {
    try {
      let id = sessionStorage.getItem(STORAGE_KEY);
      if (!id) {
        id = 'tab-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
        sessionStorage.setItem(STORAGE_KEY, id);
      }
      return id;
    } catch {
      return 'tab-anon';
    }
  }

  // ── Refresh coordinator ──
  // Tuning is overridable (window.HUB_REFRESH_TUNING) so tests can shrink
  // the windows; production always uses the defaults.
  const TUNE = (typeof window.HUB_REFRESH_TUNING === 'object' && window.HUB_REFRESH_TUNING) || {};
  const REFRESH_DEBOUNCE_MS = TUNE.debounceMs || 1000;          // settle after the last change
  const REFRESH_MAX_WAIT_MS = TUNE.maxWaitMs || 6000;           // fire even if changes keep arriving
  const REFRESH_MIN_INTERVAL_MS = TUNE.minIntervalMs || 5000;   // at most one refresh per interval
  const RUN_HOLD_CAP_MS = TUNE.runHoldCapMs || 180000;          // safety cap per agent-run hold
  const TOAST_MIN_INTERVAL_MS = TUNE.toastMinIntervalMs || 4000;

  let refreshTimer = null;
  let refreshFirstQueuedAt = 0;
  let refreshLastFiredAt = 0;
  let refreshPendingEvt = null;
  let refreshHeldQueued = false;
  const activeRuns = new Map(); // runId -> hold-cap timeout

  function refreshHeld() { return activeRuns.size > 0; }

  function fireRefresh() {
    refreshTimer = null;
    refreshFirstQueuedAt = 0;
    if (refreshHeld()) { refreshHeldQueued = true; return; }
    refreshLastFiredAt = Date.now();
    const evt = refreshPendingEvt;
    refreshPendingEvt = null;
    try {
      if (typeof window.hubPageRefresh === 'function') { window.hubPageRefresh(evt || undefined); return; }
    } catch (err) { console.warn('hubPageRefresh failed', err); }
    try {
      // Soft and QUIET only — never window.location.reload() from here, and
      // no shell swap or skeleton: the page re-renders in place. If the route
      // isn't soft-reloadable this is a no-op and the page simply stays.
      if (typeof window.hubSoftReload === 'function') window.hubSoftReload({ fallback: false, quiet: true, source: 'hub-events' });
    } catch (err) { console.warn('hubSoftReload failed', err); }
  }

  function requestRefresh(evt) {
    if (evt) refreshPendingEvt = evt;
    const now = Date.now();
    if (refreshTimer) {
      // Already queued — coalesce. Extend the settle window unless the
      // max-wait cap says the pending refresh should fire regardless.
      if (refreshFirstQueuedAt && now - refreshFirstQueuedAt >= REFRESH_MAX_WAIT_MS) return true;
      clearTimeout(refreshTimer);
      const capRemaining = refreshFirstQueuedAt
        ? Math.max(0, REFRESH_MAX_WAIT_MS - (now - refreshFirstQueuedAt))
        : REFRESH_MAX_WAIT_MS;
      refreshTimer = setTimeout(fireRefresh, Math.min(REFRESH_DEBOUNCE_MS, capRemaining));
      return true;
    }
    refreshFirstQueuedAt = now;
    const sinceLast = now - refreshLastFiredAt;
    const wait = Math.max(REFRESH_DEBOUNCE_MS, REFRESH_MIN_INTERVAL_MS - sinceLast);
    refreshTimer = setTimeout(fireRefresh, Math.min(wait, REFRESH_MAX_WAIT_MS));
    return true;
  }
  window.hubRequestRefresh = requestRefresh;

  // ── Agent-run lifecycle (backend-owned) ──
  // The Hub backend emits {kind:'agent-run', id, op:'started'|'completed'|
  // 'failed', changed?} around every OpenClaw handoff. While any run is
  // active, watcher-driven refreshes are held; on completion they flush as
  // one refresh. Failed runs drop the held refreshes (post-failure writes
  // re-arrive unheld and refresh normally).
  function endRun(runId, evt, doRefresh) {
    const cap = activeRuns.get(runId);
    if (cap) clearTimeout(cap);
    activeRuns.delete(runId);
    if (doRefresh) requestRefresh(evt || undefined);
    if (!activeRuns.size && refreshHeldQueued) {
      refreshHeldQueued = false;
      if (evt && evt.op === 'completed') requestRefresh();
    }
  }

  // Map agent "changed: kind/id" hints onto watcher event kinds.
  const HINT_KIND_ALIASES = {
    docs: 'wiki', doc: 'wiki', wiki: 'wiki',
    people: 'person', person: 'person',
    'people-groups': 'people-group', 'people-group': 'people-group',
    events: 'event', event: 'event',
    tasks: 'task', task: 'task',
    projects: 'project', project: 'project',
    companies: 'company', company: 'company',
    campaigns: 'campaign', campaign: 'campaign',
    gallery: 'gallery', files: 'file', file: 'file',
    brand: 'brand', icp: 'brand', icps: 'brand',
  };

  function hintRelevant(changed) {
    const entity = currentEntity();
    if (!entity) return false;
    for (const raw of changed) {
      const s = String(raw || '').trim();
      const slash = s.indexOf('/');
      if (slash <= 0) continue;
      const kindRaw = s.slice(0, slash).toLowerCase();
      const kind = HINT_KIND_ALIASES[kindRaw] || kindRaw;
      const id = s.slice(slash + 1);
      if (relevant(entity, { kind, id })) return true;
    }
    return false;
  }

  function handleAgentRunEvent(evt) {
    const runId = evt.id || 'run';
    try { window.dispatchEvent(new CustomEvent('hub:agent-run', { detail: evt })); } catch {}
    if (evt.op === 'started') {
      if (activeRuns.has(runId)) return;
      const cap = setTimeout(() => {
        // Terminal frame lost (server restart, dropped SSE) — release the
        // hold so watcher refreshes aren't starved forever.
        endRun(runId, null, false);
      }, RUN_HOLD_CAP_MS);
      activeRuns.set(runId, cap);
      return;
    }
    if (evt.op === 'completed') {
      const changed = Array.isArray(evt.changed) ? evt.changed : [];
      // With hints: refresh only when a hinted resource matches this page.
      // Without hints: rely on the held watcher events — the watcher is the
      // ground truth for "content actually changed", so pure-Q&A runs don't
      // trigger a pointless refresh.
      endRun(runId, evt, changed.length ? hintRelevant(changed) : false);
      return;
    }
    if (evt.op === 'failed') {
      endRun(runId, evt, false);
    }
  }

  // Touchfile scope → pages it concerns. Scopes are the touchfile basenames
  // agents write under vault/.refresh/. Unknown scopes (and 'all') keep the
  // old broadcast-to-everyone behavior; dashboards always refresh.
  const TOUCH_SCOPE_ALWAYS_PATHS = ['/', '/dashboard', '/my-work', '/home'];
  const TOUCH_SCOPE_PATHS = {
    events: [/^\/events?(\/|$)/, /^\/conferences(\/|$)/],
    people: [/^\/people(-groups?)?(\/|$)/, /^\/person(\/|$)/, /^\/groups(\/|$)/],
    groups: [/^\/people(-groups?)?(\/|$)/, /^\/groups(\/|$)/],
    wiki: [/^\/docs(\/|$)/, /^\/document(\/|$)/],
    gallery: [/^\/gallery(\/|$)/],
    files: [/^\/files(\/|$)/],
    tasks: [/^\/tasks?(\/|$)/, /^\/board(\/|$)/],
    projects: [/^\/projects?(\/|$)/],
    companies: [/^\/compan(y|ies)(\/|$)/],
    campaigns: [/^\/campaigns?(\/|$)/],
  };
  function touchScopeRelevant(scope) {
    // Scope ids may keep the touchfile extension ("people.touch") — strip it.
    const key = String(scope || 'all').replace(/\.[a-z0-9]+$/i, '').toLowerCase();
    if (!key || key === 'all') return true;
    const path = (location.pathname.replace(/\/+$/, '') || '/');
    if (TOUCH_SCOPE_ALWAYS_PATHS.includes(path)) return true;
    const pats = TOUCH_SCOPE_PATHS[key];
    if (!pats) return true;
    return pats.some((re) => re.test(path));
  }

  const handlers = new Map(); // kind -> Set<fn>
  let es = null;
  let connected = false;

  function dispatchHandlers(evt) {
    const set = handlers.get(evt.kind);
    if (set) set.forEach((fn) => { try { fn(evt); } catch (err) { console.error('hubEvents handler error', err); } });
    const all = handlers.get('*');
    if (all) all.forEach((fn) => { try { fn(evt); } catch (err) { console.error('hubEvents handler error', err); } });
  }

  function open() {
    if (es) return;
    const url = '/api/events/stream?cid=' + encodeURIComponent(clientId());
    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (err) {
      console.warn('hubEvents: EventSource construct failed', err);
      return;
    }
    es.addEventListener('open', () => { connected = true; });
    es.addEventListener('error', () => { connected = false; /* native auto-reconnect */ });
    es.addEventListener('message', (msgEv) => {
      let evt;
      try { evt = JSON.parse(msgEv.data); } catch { return; }
      if (!evt || !evt.kind) return;
      // Agent-run lifecycle frames manage the refresh hold; they are not
      // vault changes, so they skip hub:vault:change entirely.
      if (evt.kind === 'agent-run') {
        handleAgentRunEvent(evt);
        dispatchHandlers(evt);
        return;
      }
      try {
        window.dispatchEvent(new CustomEvent('hub:vault:change', { detail: evt }));
      } catch {}
      // Touchfile broadcasts (server emits kind:'refresh' on writes to
      // vault/.refresh/<scope>.touch) fan out as a generic + scoped DOM
      // event so any page can opt in regardless of its entity kind.
      if (evt.kind === 'refresh') {
        const scope = evt.id || 'all';
        try {
          window.dispatchEvent(new CustomEvent('hub:refresh', { detail: { scope, evt } }));
          window.dispatchEvent(new CustomEvent(`hub:refresh:${scope}`, { detail: { evt } }));
        } catch {}
        // Route through the coordinator: coalesced, held during agent runs,
        // never a hard reload — and only when the touchfile's scope concerns
        // the page the user is on (a people.touch must not repaint /events).
        if (touchScopeRelevant(scope)) requestRefresh(evt);
        return;
      }
      dispatchHandlers(evt);
    });
  }

  function close() {
    if (es) { try { es.close(); } catch {} es = null; }
    connected = false;
  }

  // Suspend when the tab is hidden for a long time so we don't hold
  // pointless connections open. Reopen on visibilitychange.
  let hiddenTimer = null;
  function onVisibility() {
    if (document.hidden) {
      if (hiddenTimer) clearTimeout(hiddenTimer);
      hiddenTimer = setTimeout(close, 5 * 60_000);
    } else {
      if (hiddenTimer) { clearTimeout(hiddenTimer); hiddenTimer = null; }
      open();
    }
  }
  document.addEventListener('visibilitychange', onVisibility);

  window.hubEvents = {
    on(kind, fn) {
      if (!kind || typeof fn !== 'function') return () => {};
      if (!handlers.has(kind)) handlers.set(kind, new Set());
      handlers.get(kind).add(fn);
      return () => handlers.get(kind)?.delete(fn);
    },
    off(kind, fn) { handlers.get(kind)?.delete(fn); },
    clientId,
    isConnected() { return !!connected; },
    open,
    close,
  };

  open();

  // ── Default page-level reaction ──
  // Detect from the URL which entity (kind, id) the user is currently
  // viewing, and on a matching vault change show a toast (throttled) and
  // request one coalesced soft refresh via the coordinator above.
  function currentEntity() {
    const path = location.pathname.replace(/\/+$/, '');
    // Detail pages
    let m;
    if ((m = path.match(/^\/tasks?\/([^/]+)/))) return { kind: 'task', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/events?\/([^/]+)/))) return { kind: 'event', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/person\/([^/]+)/))) return { kind: 'person', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/people\/([^/]+)/))) return { kind: 'person', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/company\/([^/]+)/))) return { kind: 'company', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/companies\/([^/]+)/))) return { kind: 'company', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/campaign\/([^/]+)/))) return { kind: 'campaign', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/docs\/(.+)/))) return { kind: 'wiki', id: decodeURIComponent(m[1]) };
    // List pages — match any change of that kind.
    if (path === '/tasks' || path === '/board' || path === '/dashboard') return { kind: 'task', id: '*' };
    if (path === '/events') return { kind: 'event', id: '*' };
    // The people page renders both people AND groups (group sections,
    // member counts); list-page entities accept either kind via
    // relevant()'s alias map below.
    if (path === '/people') return { kind: 'person', id: '*' };
    if ((m = path.match(/^\/people-group\/([^/]+)/))) return { kind: 'people-group', id: decodeURIComponent(m[1]) };
    if ((m = path.match(/^\/people-groups\/([^/]+)/))) return { kind: 'people-group', id: decodeURIComponent(m[1]) };
    if (path === '/companies') return { kind: 'company', id: '*' };
    if (path === '/campaigns') return { kind: 'campaign', id: '*' };
    if (path === '/gallery') return { kind: 'gallery', id: '*' };
    if (path === '/files') return { kind: 'file', id: '*' };
    if (path === '/docs') return { kind: 'wiki', id: '*' };
    if (path === '/projects') return { kind: 'project', id: '*' };
    if (path === '/brand' || path.startsWith('/brand/')) return { kind: 'brand', id: '*' };
    if (path === '/conferences' || path === '/conferences/groups') return { kind: 'conference', id: '*' };
    if ((m = path.match(/^\/conferences\/(\d+)/))) return { kind: 'conference', id: 'CF' + m[1] };
    return null;
  }

  // Kinds whose change events should also refresh another kind's list page.
  // The people list page is the canonical example — it shows both people
  // and group sections, so a 'people-group' edit must refresh '/people'.
  const KIND_ALIASES_FOR_LIST = {
    person: ['people-group'],
  };

  // Watcher events carry prefixed vault ids (E23, P86, T12) while URLs use
  // bare numbers (/events/23). Compare with the kind-letter prefix stripped
  // from either side when the remainder is numeric.
  function idMatches(a, b) {
    const x = String(a || '').toLowerCase();
    const y = String(b || '').toLowerCase();
    if (x === y) return true;
    const strip = (s) => s.replace(/^[a-z]{1,3}(?=\d+$)/, '');
    return strip(x) === strip(y);
  }

  function relevant(entity, evt) {
    if (!entity) return false;
    if (entity.kind === evt.kind) {
      if (entity.id === '*') return true;
      return idMatches(entity.id, evt.id);
    }
    // Aliased kinds only apply to list pages (id === '*').
    if (entity.id === '*' && KIND_ALIASES_FOR_LIST[entity.kind]?.includes(evt.kind)) return true;
    return false;
  }

  function kindLabel(kind) {
    return {
      task: 'Task', wiki: 'Doc', project: 'Project',
      person: 'Person', 'people-group': 'Group',
      event: 'Event', campaign: 'Campaign',
      gallery: 'Image', file: 'File', company: 'Company',
      brand: 'Brand', conference: 'Conference',
    }[kind] || kind;
  }

  let lastToastAt = 0;
  window.addEventListener('hub:vault:change', (e) => {
    const evt = e.detail;
    if (!evt || evt.kind === 'agent-run' || evt.kind === 'refresh') return;
    const entity = currentEntity();
    if (!relevant(entity, evt)) return;
    // Toast is throttled so a burst (agent writing many files, CLI loops)
    // shows one notice instead of one per file.
    const now = Date.now();
    if (now - lastToastAt >= TOAST_MIN_INTERVAL_MS) {
      lastToastAt = now;
      const label = kindLabel(evt.kind);
      const verb = evt.op === 'create' ? 'created' : evt.op === 'delete' ? 'deleted' : 'updated';
      try { window.hubToast?.info?.(`${label} ${evt.id} ${verb}`); } catch {}
    }
    requestRefresh(evt);
  });
})();
